// Compact inline editors for the settings tables. Kept separate from
// components/ui.jsx inputs because table cells need a 28px height and,
// for the productivity heatmap, a transparent background.
import { useEffect, useState } from "react";
import { ChevronDown } from "lucide-react";
import SearchableSelect from "../../../../components/ui/SearchableSelect";

const CELL_CLS =
  "h-7 w-full rounded-[4px] border px-2 text-[12px] text-text_primary placeholder:text-text_muted " +
  "focus:outline-none focus:border-border_strong focus-visible:ring-2 focus-visible:ring-border disabled:cursor-not-allowed";

const surfaceCls = (transparent, disabled) =>
  transparent ? "bg-transparent border-transparent" : disabled ? "bg-transparent border-transparent" : "bg-surface border-border";

/**
 * Number cell that keeps the typed text (so "0." or "1.2" can be typed) and
 * only reports finite numbers upward.
 */
export function CellNumber({ value, onChange, disabled, step = "any", min, max, ariaLabel, className = "", style, transparent, suffix }) {
  const [text, setText] = useState(value == null ? "" : String(value));

  useEffect(() => {
    setText((cur) => (Number(cur) === Number(value) && cur !== "" ? cur : value == null ? "" : String(value)));
  }, [value]);

  return (
    <div className="relative" style={style}>
      <input
        type="number"
        inputMode="decimal"
        step={step}
        min={min}
        max={max}
        value={text}
        disabled={disabled}
        aria-label={ariaLabel}
        onChange={(e) => {
          setText(e.target.value);
          const n = Number(e.target.value);
          if (e.target.value !== "" && Number.isFinite(n)) onChange?.(n);
        }}
        onBlur={() => setText(value == null ? "" : String(value))}
        className={`${CELL_CLS} ${surfaceCls(transparent, disabled)} text-right tabular-nums ${suffix ? "pr-6" : ""} ${className}`}
      />
      {suffix && <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-[11px] text-text_muted">{suffix}</span>}
    </div>
  );
}

/** Edits a fraction (0.15) as a percentage (15 %). */
export function CellPct({ value, onChange, ...props }) {
  return (
    <CellNumber
      value={value == null ? null : +(Number(value) * 100).toFixed(4)}
      onChange={(n) => onChange?.(+(n / 100).toFixed(6))}
      suffix="%"
      step="0.1"
      {...props}
    />
  );
}

export function CellText({ value, onChange, disabled, ariaLabel, className = "", placeholder }) {
  return (
    <input
      type="text"
      value={value ?? ""}
      disabled={disabled}
      placeholder={placeholder}
      aria-label={ariaLabel}
      onChange={(e) => onChange?.(e.target.value)}
      className={`${CELL_CLS} ${surfaceCls(false, disabled)} ${className}`}
    />
  );
}

/**
 * Dropdown cell: renders as plain text until clicked, then mounts a compact
 * SearchableSelect with its menu open. Avoids mounting hundreds of react-selects.
 */
export function InlineSelect({ value, options, onChange, disabled, ariaLabel, placeholder = "—", minWidth = 150 }) {
  const [editing, setEditing] = useState(false);
  const label = options.find((o) => String(o.value) === String(value))?.label;

  if (editing && !disabled) {
    return (
      <div style={{ minWidth }}>
        <SearchableSelect
          compact
          autoFocus
          defaultMenuIsOpen
          options={options}
          value={value}
          ariaLabel={ariaLabel}
          onChange={(v) => {
            onChange?.(v);
            setEditing(false);
          }}
          onMenuClose={() => setEditing(false)}
        />
      </div>
    );
  }

  return (
    <button
      type="button"
      disabled={disabled}
      aria-label={ariaLabel ? `${ariaLabel}: ${label || placeholder}` : undefined}
      onMouseDown={(e) => e.preventDefault()}
      onClick={() => setEditing(true)}
      style={{ minWidth }}
      className={`${CELL_CLS} ${surfaceCls(false, disabled)} flex items-center justify-between gap-2 text-left disabled:opacity-100 ${disabled ? "" : "cursor-pointer"}`}
    >
      <span className={`truncate ${label ? "" : "text-text_muted"}`}>{label || placeholder}</span>
      {!disabled && <ChevronDown size={12} className="flex-shrink-0 text-text_muted" aria-hidden="true" />}
    </button>
  );
}
